import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { serviceTokenStore } from "../../utils/serviceTokenStore";
import Header from "../../containers/Header";
import Button from "../../components/shared/Button";
import Loader from "../../components/shared/Loader";
import CompletedBadge from "../../components/shared/CompletedBadge";
import BubblingAIAssistant from "../../components/BubblingAIAssistant";
import { useAppContext } from "../../context/AppContext";
import { useLocalization } from "../../context/LocalizationContext";
import { convertToArabicNumerals } from "../../utils/helpers";
import { type CategoryGroup, GROUP_TITLE_KEYS } from "../../apis/questionnaire";
import arrow from "../../assets/icons/arrow.svg";
import "./styles.css";
import { updateCustomAvatarContext } from "../../apis/customAvatar";
import { useAIAvatarSession } from "../../context/AIAvatarSessionContext";
import { ASSESSMENT_CUSTOM_AVATAR_IDS } from "../../constants";

interface AssessmentProps {
  userData?: unknown;
}

const Assessment: React.FC<AssessmentProps> = ({ userData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    setCurrentCategory,
    setCurrentGroup,
    categoryGroups,
    categories,
    savedAnswers,
    loadCategoryGroups,
    isLoadingCategories,
    categoriesError,
    completedCategories,
  } = useAppContext();
  const { sessionId, repeat, interruptSpeech } = useAIAvatarSession();

  const { t, isRTL } = useLocalization();
  const userDataFromState = location.state?.userData || userData;

  const [selectedGroup, setSelectedGroup] = useState<CategoryGroup | null>(null);

  useEffect(() => {
    if (categoryGroups.length === 0 && !isLoadingCategories && serviceTokenStore.getToken()) {
      loadCategoryGroups();
    }
  }, [categoryGroups.length, isLoadingCategories, loadCategoryGroups]);

  const handleGroupSelect = (group: CategoryGroup) => {
    setCurrentGroup(group.type);
    setSelectedGroup(group);
    updateCustomAvatarContext(sessionId, group.type, "current", isRTL ? "ar" : "en").then((msg) => {
      interruptSpeech("");
      repeat(msg)
    });
  };

  const handleAssessmentClick = (assessment: { id: number; name: string; title: string }) => {
    setCurrentCategory(assessment.id);
    navigate(`/assessment/${assessment.id}`, {
      state: { assessment, userData: userDataFromState },
    });
    updateCustomAvatarContext(sessionId, assessment.title, "current", isRTL ? "ar" : "en").then((msg) => {
      interruptSpeech("");
      repeat(msg)
    });
  };

  const handleBack = () => {
    setSelectedGroup(null);
  };

  if (isLoadingCategories) {
    return (
      <div className="page-container scrollable-page">
        <Loader text={t("loadingAssessments")} />
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="page-container scrollable-page">
        <div className="error-message">{categoriesError}</div>
      </div>
    );
  }

  // ─── Assessment list view (after selecting a group) ──────────────────────
  if (selectedGroup) {
    const groupAssessments = selectedGroup.assessments;
    const allGroupCompleted = groupAssessments.every(a =>
      completedCategories.includes(a.id)
    );
    const nextAssessment = groupAssessments.find(a =>
      !completedCategories.includes(a.id)
    );

    return (
      <div className="page-container scrollable-page">
        <div className="assessment-content">
          <Header
            title={t("assessment.title")}
            statusText={`${t("loggedInAs")} ${(userDataFromState as { name?: string })?.name || ""}`.trim()}
            showHomeButton
            showSettingsButton
          />

          <button className="group-back-btn" onClick={handleBack} aria-label={t("back")}>
            <img
              src={arrow}
              alt=""
              style={{ transform: isRTL ? "none" : "rotate(180deg)", width: "3vw" }}
            />
            <span>{t(GROUP_TITLE_KEYS[selectedGroup.type])}</span>
          </button>

          <div className="assessments-list">
            {groupAssessments.map((assessment) => {
              const categoryAnswers = savedAnswers[assessment.id];
              const answeredCount = categoryAnswers ? Object.keys(categoryAnswers).length : 0;
              const isCompleted = completedCategories.includes(assessment.id);
              return (
                <Button
                  key={assessment.id}
                  variant="card"
                  className="assessment-item assessment-section"
                  onClick={() => handleAssessmentClick(assessment)}
                >
                  <div className="assessment-info">{assessment.title}</div>
                  {isCompleted ? (
                    <CompletedBadge />
                  ) : (
                    <div className="assessment-question-count">
                      {isRTL
                        ? `${convertToArabicNumerals(answeredCount)}/${convertToArabicNumerals(assessment.questionCount)}`
                        : `${answeredCount}/${assessment.questionCount}`}
                    </div>
                  )}
                </Button>
              );
            })}
          </div>

          {!allGroupCompleted && nextAssessment && (
            <div className="assessment-page-footer">
              <Button
                variant="solid"
                size="lg"
                className="get-started-btn"
                onClick={() => handleAssessmentClick(nextAssessment)}
              >
                {t("assessment.getStarted")}
              </Button>
            </div>
          )}
        </div>
        <BubblingAIAssistant />
      </div>
    );
  }

  // ─── Group selection view (Figma 2×2 grid) ───────────────────────────────
  return (
    <div className="page-container scrollable-page">
      <div className="assessment-content">
        <Header
          title={t("assessment.title")}
          statusText={`${t("loggedInAs")} ${(userDataFromState as { name?: string })?.name || ""}`.trim()}
          showHomeButton
          showSettingsButton
        />

        <div className="group-grid">
          {categoryGroups.map((group) => {
            const count = group.assessments.length;
            const allCompleted = group.assessments.every(a =>
              completedCategories.includes(a.id)
            );

            return (
              <div
                key={group.type}
                className={`group-card ${allCompleted ? "completed" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleGroupSelect(group)}
                onKeyDown={(e) => e.key === "Enter" && handleGroupSelect(group)}
              >
                <span className="group-card-title">
                  {t(GROUP_TITLE_KEYS[group.type])}
                </span>
                <div className="group-card-footer">
                  {allCompleted ? (
                    <CompletedBadge className="group-check" />
                  ) : (
                    <span className="group-card-count">
                      {isRTL
                        ? `${convertToArabicNumerals(count)} ${t("assessment.assessments")}`
                        : `${count} ${t("assessment.assessments")}`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <BubblingAIAssistant />
    </div>
  );
};

export default Assessment;
